import type { TFunction } from "@heylife/i18n";

export interface FieldIssue {
  path: string;
  code?: string;
  format?: string;
  message?: string;
}

/** Readable text from an API error body. Zod failures arrive as objects, which otherwise render as "[object Object]". */
export function readErrorMessage(body: unknown, fallback = ""): string {
  if (typeof body === "string") {
    const text = body.trim();
    return text && text !== "[object Object]" ? text : fallback;
  }
  if (!body || typeof body !== "object") return fallback;
  const rec = body as Record<string, unknown>;
  const nested =
    rec.error && typeof rec.error === "object" ? (rec.error as Record<string, unknown>) : null;
  const fromIssues = issueLines(rec.issues) || (nested ? issueLines(nested.issues) : "");
  if (fromIssues) return fromIssues;
  const direct =
    stringField(rec.error) ||
    stringField(rec.message) ||
    (nested ? stringField(nested.message) : "");
  return direct || fallback;
}

export function messageFromUnknown(error: unknown, fallback = "error"): string {
  if (typeof error === "string") {
    const text = error.trim();
    if (text && text !== "[object Object]") return text;
  }
  if (error instanceof Error && error.message && error.message !== "[object Object]")
    return error.message;
  return readErrorMessage(error, fallback);
}

function stringField(value: unknown): string {
  if (typeof value !== "string") return "";
  const text = value.trim();
  return text && text !== "[object Object]" ? text : "";
}

function issueLines(issues: unknown): string {
  return readFieldIssues({ issues })
    .map((issue) => {
      const detail = issue.message || issue.code || "";
      if (issue.path && detail) return `${issue.path}: ${detail}`;
      return detail || issue.path;
    })
    .filter(Boolean)
    .join("\n");
}

/** Zod issues from an API body, or from an error that already carries them. */
export function readFieldIssues(body: unknown): FieldIssue[] {
  if (!body || typeof body !== "object") return [];
  const rec = body as Record<string, unknown>;
  const top = normalizeIssues(rec.issues);
  if (top.length) return top;
  const nested = rec.error && typeof rec.error === "object" ? (rec.error as Record<string, unknown>) : null;
  return nested ? normalizeIssues(nested.issues) : [];
}

/** Sentence a person can act on. Known codes and Zod issues are translated; a message that is already a sentence is kept. */
export function friendlyAdminError(t: TFunction, error: unknown): string {
  const issues = readFieldIssues(error);
  if (issues.length) {
    const lines = unique(issues.map((issue) => friendlyIssue(t, issue)));
    if (lines.length) return lines.slice(0, 4).join("\n");
  }
  const raw = messageFromUnknown(error, "");
  const lines = raw
    .split("\n")
    .map((line) => friendlyLine(t, line.trim()))
    .filter(Boolean);
  return unique(lines).slice(0, 4).join("\n") || fallback(t);
}

function friendlyLine(t: TFunction, line: string): string {
  if (!line || line === "[object Object]") return fallback(t);
  if (/^[a-z0-9_:-]+$/i.test(line)) return codeText(t, line) ?? fallback(t);
  const split = line.match(/^([a-zA-Z0-9_.]+):\s+(.+)$/);
  if (split?.[1] && split[2]) return friendlyIssue(t, { path: split[1], message: split[2] });
  if (looksTechnical(line) || /^\d{3}$/.test(line)) return fallback(t);
  return line;
}

function friendlyIssue(t: TFunction, issue: FieldIssue): string {
  const path = issue.path;
  const message = issue.message ?? "";
  const code = issue.code ?? "";
  const format = issue.format ?? "";
  if (/^[a-z0-9_]+$/.test(message)) {
    const coded = codeText(t, message);
    if (coded) return coded;
  }
  const slugField = path === "slug" || path.endsWith(".slug");
  const pattern = format === "regex" || /pattern|invalid string|regex/i.test(message);
  if (slugField && (pattern || code === "too_small" || code === "invalid_format" || !message)) return text(t, "admin.errors.slug");
  if (pattern && slugField) return text(t, "admin.errors.slug");
  if (format === "email" || /email/i.test(path)) return text(t, "admin.errors.email");
  if (format === "url" || path.toLowerCase().endsWith("url")) return text(t, "admin.errors.url", { field: fieldLabel(t, path) });
  if (code === "too_small" || code === "invalid_type" || /too small|expected string to have|at least|received undefined/i.test(message)) {
    if (slugField) return text(t, "admin.errors.slug");
    return text(t, "admin.errors.required", { field: fieldLabel(t, path) });
  }
  if (code === "too_big" || /too big/i.test(message)) return text(t, "admin.errors.tooLong", { field: fieldLabel(t, path) });
  if (path) return text(t, "admin.errors.invalid", { field: fieldLabel(t, path) });
  if (looksTechnical(message)) return fallback(t);
  return message || fallback(t);
}

function fieldLabel(t: TFunction, path: string): string {
  return translated(t, `admin.errors.fields.${path.replaceAll(".", "_")}`) ?? humanize(path);
}

function codeText(t: TFunction, code: string): string | null {
  return translated(t, `admin.errors.codes.${code}`) ?? translated(t, `admin.errors.codes.${code.split(":")[0] ?? code}`);
}

function text(t: TFunction, key: string, values?: Record<string, unknown>): string {
  return translated(t, key, values) ?? fallback(t);
}

function translated(t: TFunction, key: string, values?: Record<string, unknown>): string | null {
  const msg = t(key, values);
  if (!msg || msg === key) return null;
  return msg;
}

function fallback(t: TFunction): string {
  return translated(t, "admin.errors.generic") ?? "Something went wrong. Please try again.";
}

function humanize(path: string): string {
  const last = path.split(".").pop() ?? path;
  const words = last.replace(/[_-]/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words || path;
}

function looksTechnical(value: string): boolean {
  return /\[object Object\]|Invalid string|must match pattern|Too small|Too big|expected string|ZodError|invalid_format|\^\[/.test(value);
}

function unique(lines: string[]): string[] {
  return [...new Set(lines.filter(Boolean))];
}

function normalizeIssues(value: unknown): FieldIssue[] {
  if (!Array.isArray(value)) return [];
  const issues: FieldIssue[] = [];
  for (const row of value) {
    if (!row || typeof row !== "object") continue;
    const issue = row as { path?: unknown; code?: unknown; format?: unknown; message?: unknown };
    const path = Array.isArray(issue.path)
      ? issue.path.map((part) => String(part)).filter((part) => part !== "").join(".")
      : typeof issue.path === "string"
        ? issue.path
        : "";
    issues.push({ path, code: stringField(issue.code) || undefined, format: stringField(issue.format) || undefined, message: stringField(issue.message) || undefined });
  }
  return issues;
}
